var _ = require('underscore');
var SecureMessageWidget = require('./../../secure-message/SecureMessageWidget');
var SecureMessageAppointmentView = require('./SecureMessageAppointmentView');
var AppointmentTypeDataBehavior = require('./../../appointment-type/AppointmentTypeDataBehavior');

var VIEW_NAME = 'eregPatientAppointmentsListWidget';
var TWO_MINUTES_IN_MS = 2 * 60 * 1000;
var APPOINTMENT_TIME_ATTRIBUTE = 'appointmentDTO.externalAppointment.time';

/**
 * Customizations to the SecureMessageWidget specific to the appointment list page in e-reg.
 * @class EregPatientAppointmentsListWidget
 * @constructor
 * @extends SecureMessageWidget
 * @param options {Object}
 *   @param [options.userIsStaff] {Boolean} whether to show staff view or patient view
 *   @param [options.patientVecnaId] {String} the vecna id of the patient.
 */
module.exports = SecureMessageWidget.extend({
  className: SecureMessageWidget.prototype.className + ' ' + VIEW_NAME,
  moduleNamespace: VIEW_NAME,

  REGULAR_ONLY: 'regular-only',
  PLACEHOLDER_ONLY: 'placeholder-only',
  BOTH: 'both',

  loadingMessageKey: 'ereg.' + VIEW_NAME + '.loading',
  fetchFailedMessageKey: 'ereg.' + VIEW_NAME + '.fetchFailed',

  itemView: SecureMessageAppointmentView,

  behaviors: _.extend({}, SecureMessageWidget.prototype.behaviors, {
    /**
     * This behavior is used to fill the cache before item views go out and make duplicate requests
     */
    appointmentTypes: {
      behavior: AppointmentTypeDataBehavior,
      updateEvents: 'behaviors.secureMessage.data.privateCollection:add',
      ids: { property: 'behaviors.secureMessage.data:appointmentDTO.externalAppointment.type'}
    }
  }),

  /**
   * @override
   * @method initialize
   * @param options {Object}
   *   @param [options.userIsStaff] {Boolean} whether to show staff view or patient view
   *   @param [options.patientVecnaId] {String} the vecna id of the patient.
   *   @param options.appointmentTypes {String} must be REGULAR_ONLY, PLACEHOLDER_ONLY, or BOTH. Default is REGULAR_ONLY.
   */
  initialize: function(options) {
    options = options || {};
    options.itemContext = options.itemContext || {};
    this._appointmentTypes = options.appointmentTypes ? options.appointmentTypes : this.REGULAR_ONLY;

    if (options.userIsStaff) {
      _.extend(options.itemContext, { userIsStaff : options.userIsStaff });
      this.instructionsMessageKey = null;
    } else {
      this.instructionsMessageKey = 'ereg.' + VIEW_NAME + '.instructions';
    }

    if (this._appointmentTypes === this.REGULAR_ONLY && options.userIsStaff) {
      this.noDataMessageKey = 'ereg.' + VIEW_NAME + '.noMessagesStaff';
    } else {
      this.noDataMessageKey = 'ereg.' + VIEW_NAME + '.noMessages';
    }

    this.headerMessageKey = 'ereg.' + VIEW_NAME + '.header';
    if (this._appointmentTypes === this.PLACEHOLDER_ONLY) {
      this.headerMessageKey = 'ereg.' + VIEW_NAME + '.placeholders.header';
    }

    /**
     * The MixedMessageCriteria selecting the messages to display.
     *
     * @property criteria {Object}
     *   @property criteria.isPatient {Boolean} username restrictions apply to patient usernames only.
     *   @property criteria.vecnaIds {Array} Array of vecnaId restrictions
     *   @property criteria.appointmentCriteria {Object} AppointmentMessageCriteria - restrictions on appointment messages.
     *     @property criteria.appointmentCriteria.externalAppointmentCriteria {Object} restrictions on the external appointment.
     *       @property criteria.appointmentCriteria.externalAppointmentCriteria.cancelled {Boolean} cancelled messages not included.
     *       @property criteria.appointmentCriteria.externalAppointmentCriteria.dateCategory {String} only show appointments that are scheduled for an extended day (1800 yesterday to 0600 tomorrow).
     *       @property criteria.appointmentCriteria.externalAppointmentCriteria.vecnaInternalAppointment {Boolean} (trinary) if true, only show placeholders; if false, only show real; if null, show both.
     */
    var vecnaInternalAppointment;
    switch (this._appointmentTypes) {
      case this.PLACEHOLDER_ONLY:
        vecnaInternalAppointment = true;
        break;
      case this.REGULAR_ONLY:
        vecnaInternalAppointment = false;
        break;
      case this.BOTH:
        vecnaInternalAppointment = null;
        break;
      default:
        throw 'incorrect value for appointmentTypes: ' + this._appointmentTypes;
    }
    this.criteria = {
      isPatient: true,
      appointmentCriteria: {
        externalAppointmentCriteria: {
          cancelled: false,
          dateCategory: 'TODAY_PLUS_MINUS_SIX',
          vecnaInternalAppointment: vecnaInternalAppointment
        }
      },
      vecnaIds: [options.patientVecnaId]
    };

    _.bindAll(this, '_selectPrimaryItemView');
    SecureMessageWidget.prototype.initialize.apply(this, arguments);
    this.collection.comparator = APPOINTMENT_TIME_ATTRIBUTE;
  },

  /**
   * Override prepare to remove the instructionsMessageKey if the collection is empty
   *
   * @override
   * @method prepare
   */
  prepare: function() {
    var templateContext = SecureMessageWidget.prototype.prepare.call(this);
    if (this.collection.length <= 0) {
      templateContext.instructionsMessageKey = null;
    }
    return templateContext;
  },

  /**
   * Override postrender to select the primary item after all items have been rendered.
   * @override
   * @method postrender
   */
  postrender: function() {
    this._selectPrimaryItemView();
  },

  /**
   * @method changePatientVecnaId
   * @param patientVecnaId
   */
  changePatientVecnaId: function(patientVecnaId) {
    this.set('criteria.vecnaIds', [patientVecnaId]);
  },

  /**
   * Starts up listeners and intervals for the view.
   * @method _activate
   * @override
   */
  _activate: function() {
    this._selectPrimaryItemViewIntervalId = window.setInterval(this._selectPrimaryItemView, TWO_MINUTES_IN_MS);
    SecureMessageWidget.prototype._activate.apply(this, arguments);
  },

  /**
   * Turns off all listeners and intervals
   * @method _deactivate
   * @override
   */
  _deactivate: function() {
    SecureMessageWidget.prototype._deactivate.apply(this, arguments);
    window.clearInterval(this._selectPrimaryItemViewIntervalId);
  },

  /**
   * Figures out which list item should display as the primary one.
   * Uses the appointment's time to pick the first appointment after the current time.
   * If no appointments have a time after the current time, then the last one in the list is used.
   * @method _selectPrimaryItemView
   * @private
   */
  _selectPrimaryItemView: function() {
    var now = Date.now();
    var firstItemModelAfterNow = this.collection.chain()
      .sortBy(APPOINTMENT_TIME_ATTRIBUTE)
      .find(function(itemModel) {
        return itemModel.get(APPOINTMENT_TIME_ATTRIBUTE) >= now;
      })
      .value();
    var primaryItemView = this.getTrackedView(firstItemModelAfterNow || this.collection.last());

    if (primaryItemView) {
      primaryItemView.setPrimary();
    }
  }
});
